import { computed, nextTick, ref } from "vue";
import { padTimePart, parseTimePart, withTimePart } from "@/utils/date.js";

const MAX_BY_PART = { hour: 23, minute: 59 };
const DIGIT_KEY = /^[0-9\u06F0-\u06F9\u0660-\u0669]$/;

function selectContents(element) {
  const range = document.createRange();
  range.selectNodeContents(element);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

function readPart(date, part) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return part === "hour" ? date.getHours() : date.getMinutes();
}

function datePickerFrom(element) {
  let instance = element?.__vueParentComponent || element?.__vnode?.ctx;
  while (instance) {
    const proxy = instance.proxy;
    if (proxy && typeof proxy.updateCurrentMetaData === "function") return proxy;
    instance = instance.parent;
  }
  return null;
}

function syncPicker(picker) {
  if (picker && typeof picker.updateCurrentMetaData === "function") {
    picker.updateCurrentMetaData();
  }
}

export function useEditableTimePicker(model) {
  const failed = ref(false);

  function fallback(element, part) {
    failed.value = true;
    try {
      if (!element) return;
      element.removeAttribute("contenteditable");
      const value = readPart(model.value, part);
      if (value !== null) element.textContent = padTimePart(value);
    } catch {
      failed.value = true;
    }
  }

  function createPartProps(part) {
    let previousText = "";

    function guard(handler) {
      return (event) => {
        if (failed.value) return;
        try {
          handler(event);
        } catch {
          fallback(event?.currentTarget, part);
        }
      };
    }

    const onFocus = guard((event) => {
      const element = event.currentTarget;
      previousText = element.textContent;
      setTimeout(() => {
        if (failed.value) return;
        try {
          if (document.activeElement !== element) return;
          selectContents(element);
        } catch {
          fallback(element, part);
        }
      }, 0);
    });

    const onBlur = guard((event) => {
      const element = event.currentTarget;
      const value = parseTimePart(element.textContent, MAX_BY_PART[part]);
      if (
        value === null ||
        !Number.isInteger(value) ||
        value < 0 ||
        value > MAX_BY_PART[part]
      ) {
        element.textContent = previousText;
        return;
      }
      const next = withTimePart(model.value, part, value);
      if (!(next instanceof Date) || Number.isNaN(next.getTime())) {
        element.textContent = previousText;
        return;
      }
      model.value = next;
      element.textContent = padTimePart(value);
      const picker = datePickerFrom(element);
      nextTick(() => {
        if (failed.value) return;
        try {
          syncPicker(picker);
        } catch {
          fallback(element, part);
        }
      });
    });

    const onKeydown = guard((event) => {
      const element = event.currentTarget;
      if (event.key === "Enter") {
        event.preventDefault();
        element.blur();
        return;
      }
      if (event.key === "Escape") {
        element.textContent = previousText;
        return;
      }
      if (event.ctrlKey || event.metaKey || event.key.length > 1) return;
      const hasSelection = window.getSelection()?.toString().length > 0;
      const isFull = !hasSelection && element.textContent.length >= 2;
      if (!DIGIT_KEY.test(event.key) || isFull) event.preventDefault();
    });

    const onPaste = guard((event) => {
      event.preventDefault();
    });

    return {
      contenteditable: "true",
      inputmode: "numeric",
      role: "textbox",
      onFocus,
      onBlur,
      onKeydown,
      onPaste,
    };
  }

  const timePt = computed(() => {
    if (failed.value) return {};
    try {
      return {
        hour: createPartProps("hour"),
        minute: createPartProps("minute"),
      };
    } catch {
      failed.value = true;
      return {};
    }
  });

  return { timePt };
}
